import SectionCard from './SectionCard'
import { isLoanScenario } from './scenarioType'
import SubsectionDivider from './SubsectionDivider'
import FieldRow from './FieldRow'
import { DerivedValueBox, EditableValueBox } from './ValueBox'
import PercentDollarRow, { PercentDollarToggleControl } from './PercentDollarRow'
import { formatMoney } from './format'
import { computedFallbackUsable } from './computePayload'

// Tooltip copy is verbatim from files/sow3/spec/canvas-text.27.08.md. Four of the five come from the
// "SELLING COSTS" tool tip block; Accrued Interest's is reused from the "HOLDING COSTS" block rather
// than duplicated with new text -- it is the same field, only its section allocation moves, per
// sell_tax_applied (README.md, "Allocation is the real, writable sell_tax_applied field"). Note
// Seller Credits here is different tooltip text from Acquisition Costs' "Seller Credits (incl. Tax
// Proration)" row -- two different fields sharing a name fragment, not the same copy reused.
// Accrued Interest, Marketing, Agent Commissions and Property Tax Proration follow letter 115.
const TOOLTIPS = {
  accruedInterest:
    'Estimated interest over the project timeline, based on your loan terms and holding period.',
  marketing:
    'Estimated cost of preparing and marketing the property for sale, such as photography, video, floor plans, virtual tours, and staging. This amount is entered separately and is not included in sale closing costs. Some services may be included in the agent’s commission.',
  agentCommissions:
    'Estimated commissions paid to the real estate agents involved in the sale, calculated as a percentage of the sale price. Commission rates are negotiable.',
  sellerCredits:
    'Amount credited to the buyer at closing for costs, repairs, or other negotiated concessions.\n\n[Sale Price × Seller Credit %]',
  propertyTaxProration:
    'Arvance automatically estimates your share of property taxes using available property-tax data and your projected purchase and sale dates.',
  titleEscrowFees:
    'Estimated seller-paid settlement costs, such as escrow, recording, wire, notary, document, and applicable HOA transfer fees.',
}

// Screen-reader text for the computed figure shown while the box is blank. Our copy (no spec covers
// it), logged in verification-runs.
export const PROPERTY_TAX_COMPUTED_DESCRIPTION = 'Calculated from the purchase and sale dates'

// scenario/onFieldChange/onToggleChange/computed: see README.md ("no save endpoint yet", "the
// computed prop contract"). `computeStale`: `computed` is not for the scenario on screen (a compute
// is pending, or the last one failed) -- README.md ("Selling Costs rows"). `computedInput`: the body
// `computed` came from (useFlipCompute).
export default function SellingCostsSection({ scenario, computed, computedInput, computeStale = false, onFieldChange, onToggleChange }) {
  const s = scenario ?? {}
  const toggles = s.toggles ?? {}

  // The row exists only under the same two conditions calcAtClosing uses to fold Accrued Interest into
  // at_closing; the design draws no row otherwise (README.md, "Accrued Interest rows").
  const accruedInterestApplies = s.sell_tax_applied === 'sell' && isLoanScenario(s.scenarioType)
  // Per-row values, with the $/% toggles already resolved, are the backend's (reilm-be
  // flip/sellingCosts.js, calcSellingCostRows); absent until a route serves computed, which renders
  // the dash. The rows sum to at_closing there -- never re-add them here.
  const rows = computed?.sellingCostRows
  // A typed 0 is a real $0 override; only a blank one falls back to the computed figure
  // (computedFallbackUsable has when a stale figure may stay).
  const taxOverride = s.sell_property_tax0
  const taxOverrideBlank = taxOverride == null || taxOverride === ''
  const taxPlaceholder =
    taxOverrideBlank && computedFallbackUsable(computeStale, computedInput, 'sell_property_tax0')
      ? formatMoney(rows?.propertyTaxProration)
      : null

  return (
    <SectionCard title="Selling Costs" total={formatMoney(computed?.selling_costs)} firstRowGap={16}>
      <div className="flex flex-col">
        <FieldRow label="Marketing Costs" tooltip={TOOLTIPS.marketing}>
          <EditableValueBox money
            value={String(s.marketing2 ?? '')}
            onChange={(v) => onFieldChange('marketing2', v)}
            ariaLabel="Marketing Costs"
          />
        </FieldRow>

        {/* Subsection divider under Marketing Costs (letter 99, 7.4.2), AcquisitionCostsSection's
            pattern: 8 + 1 + 8 keeps the drawn 17px from Marketing Costs to the next row
            (flip-cash-v3: Marketing box ends 3854, Agent Commissions starts 3871). */}
        <SubsectionDivider testId="selling-marketing-divider" className="mt-[8px] mb-[8px]" />

        {/* Below the divider, above Agent Commissions (letter 99, 7.4.4.4) -- out of Marketing Costs'
            subsection. Letter 104 (1.3): with Allocation "Paid at Sale" the row reads "Interest Due at
            Sale"; Holding Costs' own row (Monthly) stays "Accrued Interest". Tooltip unchanged. */}
        {accruedInterestApplies ? (
          <FieldRow label="Interest Due at Sale" tooltip={TOOLTIPS.accruedInterest}>
            <DerivedValueBox value={formatMoney(rows?.accruedInterest)} ariaLabel="Interest Due at Sale" />
          </FieldRow>
        ) : null}

        <div className={accruedInterestApplies ? 'mt-[8px]' : undefined}>
          <FieldRow
            label="Agent Commissions"
            tooltip={TOOLTIPS.agentCommissions}
            control={
              <PercentDollarToggleControl
                toggleValue={toggles.selling_commission}
                onToggleChange={(v) => onToggleChange('selling_commission', v)}
                ariaLabel="Agent Commissions %"
              />
            }
          >
            <PercentDollarRow
              percentValue={String(s.selling_commission0 ?? '')}
              onPercentChange={(v) => onFieldChange('selling_commission0', v)}
              percentAriaLabel="Agent Commissions %"
              dollarValue={String(s.selling_commission1 ?? '')}
              onDollarChange={(v) => onFieldChange('selling_commission1', v)}
              dollarAriaLabel="Agent Commissions $"
              dollarComputedValue={formatMoney(rows?.agentCommissions)}
              toggleValue={toggles.selling_commission}
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow
            label="Seller Credits"
            tooltip={TOOLTIPS.sellerCredits}
            control={
              <PercentDollarToggleControl
                toggleValue={toggles.concessions_to_buyer}
                onToggleChange={(v) => onToggleChange('concessions_to_buyer', v)}
                ariaLabel="Seller Credits %"
              />
            }
          >
            <PercentDollarRow
              percentValue={String(s.concessions_to_buyer1 ?? '')}
              onPercentChange={(v) => onFieldChange('concessions_to_buyer1', v)}
              percentAriaLabel="Seller Credits %"
              dollarValue={String(s.concessions_to_buyer2 ?? '')}
              onDollarChange={(v) => onFieldChange('concessions_to_buyer2', v)}
              dollarAriaLabel="Seller Credits $"
              dollarComputedValue={formatMoney(rows?.sellerCredits)}
              toggleValue={toggles.concessions_to_buyer}
            />
          </FieldRow>
        </div>

        <div className="mt-[8px]">
          <FieldRow label="Property Tax Proration" tooltip={TOOLTIPS.propertyTaxProration}>
            <EditableValueBox money
              value={String(taxOverride ?? '')}
              onChange={(v) => onFieldChange('sell_property_tax0', v)}
              // Blank override -> the backend computes the tax from the dates; show that figure until
              // the user types one, drawn as a value (letter 145 item 1). README.md ("Selling Costs rows").
              placeholder={taxPlaceholder}
              placeholderDark
              placeholderDescription={PROPERTY_TAX_COMPUTED_DESCRIPTION}
              ariaLabel="Property Tax Proration"
            />
          </FieldRow>
        </div>

        {/* sell_other2 (workbook L41), typed directly: both v3 frames draw this box bordered (87 x 31,
            stroke #C6CFC6), the editable style Marketing Costs uses. The backend returns it unchanged
            as sellingCostRows.titleEscrowFees. */}
        <div className="mt-[8px]">
          <FieldRow label="Title & Escrow Fees" tooltip={TOOLTIPS.titleEscrowFees}>
            <EditableValueBox money
              value={String(s.sell_other2 ?? '')}
              onChange={(v) => onFieldChange('sell_other2', v)}
              ariaLabel="Title & Escrow Fees"
            />
          </FieldRow>
        </div>

        {/* No tooltip: legacy's caption for this row named four old labels (Agent Fees + Seller
            Concessions + Est. Property Taxes + Seller Closing Fees), which is calcAtClosing's own
            term list -- and calcAtClosing (returned as at_closing) is exactly this row's own value,
            unlike selling_costs (at_closing + marketing2), which the section's header total reads
            instead -- see README.md ("at_closing vs selling_costs"). "(Selling Costs)" disambiguates
            the testid only -- Acquisition Costs carries its own "Total Closing Costs" row too
            (README.md, "ambiguous testids across sections"); the visible label stays bare. */}
        <div className="mt-[8px]">
          <FieldRow label="Total Closing Costs" weight="bold">
            <DerivedValueBox value={formatMoney(computed?.at_closing)} bold topDivider ariaLabel="Total Closing Costs (Selling Costs)" />
          </FieldRow>
        </div>
      </div>
    </SectionCard>
  )
}
